import LoadingSpinner from "@/components/LoadingSpinner";
import useApi from "@/hooks/useApi";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Column,
  PrimaryBlueButton,
  SecondaryGrayButton,
  TableBase
} from "@velaro/velaro-component-lib";
import BulkActionBar from "@/components/BulkActionBar";
import ActionBar from "@/pages/Messaging/ChatWindow/ActionBar";
import AddFieldModal from "./AddFieldModal";
import EditFieldModal from "./EditFieldModal";

export interface FieldOption {
  label: string;
  value: string;
  id: string;
}

export interface FieldDefinition {
  type: "text" | "select" | "checkbox" | "textarea";
  options: Array<FieldOption>;
  label: string;
  scope: "workflow" | "conversation" | "contact";
  persistent: boolean;
  description: string;
}

export interface ContactFieldDef {
  id: number;
  name: string;
  label: string;
  type: string;
  options?: string;
  sortOrder: number;
  isRequired: boolean;
  scope?: string;
  persistent?: boolean;
  description?: string;
}

const scopeLabels: Record<string, string> = {
  workflow: "Workflow",
  conversation: "Conversation",
  contact: "Contact"
};

const typeLabels: Record<string, string> = {
  text: "Text",
  checkbox: "Checkbox",
  textarea: "Textarea",
  select: "Select List"
};

const columns: Column[] = [
  { label: "Label", accessor: "label" },
  { label: "Variable", accessor: "variable" },
  { label: "Type", accessor: "type" },
  { label: "Scope", accessor: "scope" },
  { label: "Persistent", accessor: "persistent" },
  { label: "", accessor: "actions", disableSort: true }
];

export default function CustomFields() {
  const api = useApi();
  const [showAddFieldModal, setShowAddFieldModal] = useState(false);
  const [editFieldId, setEditFieldId] = useState<number | undefined>();
  const [loading, setLoading] = useState(true);
  const [fields, setFields] = useState<ContactFieldDef[]>([]);
  const [maxFields, setMaxFields] = useState(10);
  const [copiedName, setCopiedName] = useState<string | null>(null);
  const [showDocs, setShowDocs] = useState(false);
  const [selectedIndices, setSelectedIndices] = useState<number[]>([]);

  useEffect(() => {
    api.messaging
      .get("ContactFieldDefinitions/list")
      .then(async (result) => {
        if (result.ok) {
          const data = await result.json();
          if (Array.isArray(data)) {
            setFields(data);
          } else {
            setFields(
              Array.isArray(data?.definitions) ? data.definitions : []
            );
            if (data?.maxCustomFields) {
              setMaxFields(data.maxCustomFields);
            }
          }
        }
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [api.messaging]);

  const removeField = useCallback(
    async (id: number) => {
      if (!window.confirm("Are you sure you want to delete this field?")) {
        return;
      }
      try {
        const resp = await api.messaging.delete(
          `ContactFieldDefinitions/${id}`
        );
        if (resp.ok) {
          setFields((prev) => prev.filter((f) => f.id !== id));
          setSelectedIndices([]);
        }
      } catch {
        // Keep existing data on failure
      }
    },
    [api.messaging]
  );

  async function bulkDelete() {
    if (
      !window.confirm(
        `Delete ${selectedIndices.length} selected field${selectedIndices.length > 1 ? "s" : ""}?`
      )
    ) {
      return;
    }
    const ids = selectedIndices.map((i) => fields[i]?.id).filter(Boolean);
    for (const id of ids) {
      try {
        await api.messaging.delete(`ContactFieldDefinitions/${id}`);
      } catch {
        // Continue deleting others
      }
    }
    setFields((prev) => prev.filter((f) => !ids.includes(f.id)));
    setSelectedIndices([]);
  }

  const submit = useCallback(
    async (definition: FieldDefinition) => {
      setShowAddFieldModal(false);

      let optionsJson: string | undefined;
      if (
        definition.type === "select" &&
        Array.isArray(definition.options) &&
        definition.options.length > 0
      ) {
        optionsJson = JSON.stringify(
          definition.options.map((o) => o.value || o.label)
        );
      }

      try {
        const resp = await api.messaging.post("ContactFieldDefinitions", {
          name: definition.label.toLowerCase().replace(/\s+/g, "_"),
          label: definition.label,
          type: definition.type,
          options: optionsJson,
          sortOrder: 0,
          isRequired: false,
          scope: definition.scope ?? "conversation",
          persistent: definition.persistent ?? false,
          description: definition.description ?? ""
        });

        if (resp.ok) {
          const created: ContactFieldDef = await resp.json();
          setFields((prev) => [...prev, created]);
        }
      } catch {
        // Keep existing data on failure
      }
    },
    [api.messaging]
  );

  const handleUpdate = useCallback(
    async (id: number, definition: FieldDefinition) => {
      setEditFieldId(undefined);

      let optionsJson: string | undefined;
      if (
        definition.type === "select" &&
        Array.isArray(definition.options) &&
        definition.options.length > 0
      ) {
        optionsJson = JSON.stringify(
          definition.options.map((o) => o.value || o.label)
        );
      }

      try {
        const resp = await api.messaging.put(`ContactFieldDefinitions/${id}`, {
          name: definition.label.toLowerCase().replace(/\s+/g, "_"),
          label: definition.label,
          type: definition.type,
          options: optionsJson,
          sortOrder: 0,
          isRequired: false,
          scope: definition.scope ?? "conversation",
          persistent: definition.persistent ?? false,
          description: definition.description ?? ""
        });

        if (resp.ok) {
          setFields((prev) =>
            prev.map((f) =>
              f.id === id
                ? {
                    ...f,
                    label: definition.label,
                    name: definition.label
                      .toLowerCase()
                      .replace(/\s+/g, "_"),
                    type: definition.type,
                    options: optionsJson,
                    scope: definition.scope,
                    persistent: definition.persistent,
                    description: definition.description
                  }
                : f
            )
          );
        }
      } catch {
        // Keep existing data on failure
      }
    },
    [api.messaging]
  );

  function copyVariable(name: string) {
    navigator.clipboard?.writeText(`<<${name}>>`);
    setCopiedName(name);
    setTimeout(() => setCopiedName(null), 2000);
  }

  const tableData = useMemo(() => {
    return fields.map((field) => ({
      label: (
        <div>
          <span className="font-medium">{field.label}</span>
          {field.description && (
            <p className="text-xs text-gray-400 mt-0.5">{field.description}</p>
          )}
        </div>
      ),
      variable: (
        <button
          className="bg-gray-100 hover:bg-cornflower-blue-100 px-2 py-0.5 rounded text-xs font-mono transition-colors"
          title={`Click to copy <<${field.name}>>`}
          aria-label={`Copy variable ${field.name}`}
          onClick={() => copyVariable(field.name)}
        >
          {"<<"}
          {field.name}
          {">>"}
          {copiedName === field.name && (
            <i
              className="fa fa-check text-green-600 ml-1"
              aria-hidden="true"
            />
          )}
        </button>
      ),
      type: typeLabels[field.type] ?? field.type,
      scope: (
        <span className="inline-block bg-gray-100 text-xs px-2 py-0.5 rounded">
          {scopeLabels[field.scope ?? "conversation"] ??
            field.scope ??
            "Conversation"}
        </span>
      ),
      persistent: field.persistent ? (
        <i className="fa fa-check text-green-600" aria-label="Persistent" />
      ) : (
        <i className="fa fa-minus text-gray-300" aria-label="Not persistent" />
      ),
      actions: (
        <ActionBar
          onEdit={() => setEditFieldId(field.id)}
          onCopy={() => copyVariable(field.name)}
          onDelete={() => removeField(field.id)}
        />
      )
    }));
  }, [fields, copiedName, removeField]);

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  const atLimit = maxFields > 0 && fields.length >= maxFields;

  return (
    <div className="h-full flex flex-col p-6">
      {/* Header */}
      <div className="flex items-center mb-4">
        <div>
          <h2 className="text-lg font-semibold">Custom Fields & Variables</h2>
          <span className="text-sm text-gray-500">
            {fields.length} / {maxFields} fields used
          </span>
        </div>
        <div className="ml-auto">
          <PrimaryBlueButton
            label="Add Field"
            disabled={atLimit}
            onClick={() => !atLimit && setShowAddFieldModal(true)}
          />
        </div>
      </div>

      {atLimit && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4 text-sm text-amber-800">
          Maximum of {maxFields} custom fields reached. Remove unused fields or
          upgrade your plan to add more.
        </div>
      )}

      {/* Bulk Action Bar */}
      {selectedIndices.length > 0 && (
        <div className="mb-4">
          <BulkActionBar>
            <div>
              ({selectedIndices.length}){" "}
              {selectedIndices.length === 1 ? "field" : "fields"} selected
            </div>
            <SecondaryGrayButton onClick={bulkDelete} label="Delete Selected" />
          </BulkActionBar>
        </div>
      )}

      {/* Fields Table */}
      {fields.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 text-gray-400 border border-gray-200 rounded-lg bg-white">
          <i className="fa fa-database text-3xl mb-3" aria-hidden="true" />
          <p className="font-medium text-gray-500">No custom fields defined</p>
          <p className="text-sm mt-1">
            Click &quot;Add Field&quot; to create your first variable.
          </p>
        </div>
      ) : (
        <div className="overflow-auto flex-1">
          <TableBase
            columns={columns}
            data={tableData}
            onChangeSelection={setSelectedIndices}
          />
        </div>
      )}

      {/* Variable Reference & Documentation — below the table */}
      <div className="bg-white border border-gray-200 rounded-lg mt-4">
        <button
          className="w-full flex items-center justify-between p-4 text-left"
          onClick={() => setShowDocs(!showDocs)}
          aria-expanded={showDocs}
          aria-controls="variable-docs"
        >
          <div>
            <span className="font-semibold text-sm">
              Variable Reference & Documentation
            </span>
            <span className="text-xs text-gray-500 ml-2">
              How to use variables in workflows, scope differences, and examples
            </span>
          </div>
          <i
            className={`fa fa-chevron-${showDocs ? "up" : "down"} text-gray-400`}
            aria-hidden="true"
          />
        </button>

        {showDocs && (
          <div id="variable-docs" className="border-t p-4 space-y-5">
            {/* Syntax */}
            <div>
              <h4 className="font-semibold text-sm mb-2">Syntax</h4>
              <p className="text-sm text-gray-600 mb-2">
                Variables use the{" "}
                <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs font-mono">
                  {"<<variable_name>>"}
                </code>{" "}
                syntax. When the workflow runs, these are replaced with the
                actual stored value. You can also use dot-notation to specify
                scope:{" "}
                <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs font-mono">
                  {"<<contact.email>>"}
                </code>
                ,{" "}
                <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs font-mono">
                  {"<<conversation.ticketId>>"}
                </code>
                ,{" "}
                <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs font-mono">
                  {"<<workflow.counter>>"}
                </code>
              </p>
            </div>

            {/* Three Scopes */}
            <div>
              <h4 className="font-semibold text-sm mb-2">
                Variable Scopes
              </h4>
              <div className="space-y-3">
                <div className="border rounded-lg p-3 bg-gray-50">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="inline-block bg-cornflower-blue-100 text-cornflower-blue-700 text-xs font-semibold px-2 py-0.5 rounded">
                      Contact
                    </span>
                    <span className="text-sm font-medium">
                      Saved forever with the customer
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 mb-2">
                    Persists across all conversations. When a returning visitor
                    starts a new chat, their contact attributes are still
                    available. Use for profile data like name, email, company,
                    customer tier, language preference.
                  </p>
                  <div className="bg-white rounded p-2 text-xs font-mono text-gray-600">
                    <span className="text-gray-400">
                      {"// Set in AskQuestion or SetData node with "}
                    </span>
                    <span className="text-cornflower-blue-500">
                      Contact Attribute
                    </span>
                    <span className="text-gray-400"> toggled on</span>
                    <br />
                    <span className="text-gray-400">
                      {"// Then use in Send Message: "}
                    </span>
                    <br />
                    Hello{" "}
                    <span className="text-cornflower-blue-500">
                      {"<<contact.firstName>>"}
                    </span>
                    , welcome back! Your tier is{" "}
                    <span className="text-cornflower-blue-500">
                      {"<<contact.customer_tier>>"}
                    </span>
                    .
                  </div>
                </div>

                <div className="border rounded-lg p-3 bg-gray-50">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="inline-block bg-green-100 text-green-700 text-xs font-semibold px-2 py-0.5 rounded">
                      Conversation
                    </span>
                    <span className="text-sm font-medium">
                      Shared across all workflows in this chat
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 mb-2">
                    Available for the entire conversation but cleared when the
                    conversation closes. Shared between all workflows that fire
                    during the same chat. Use for ticket numbers, issue category,
                    routing decisions.
                  </p>
                  <div className="bg-white rounded p-2 text-xs font-mono text-gray-600">
                    <span className="text-gray-400">
                      {"// Workflow 1 sets the ticket via SetData node:"}
                    </span>
                    <br />
                    ticket_id ={" "}
                    <span className="text-green-600">&quot;TKT-12345&quot;</span>
                    <br />
                    <span className="text-gray-400">
                      {
                        "// Workflow 2 (transfer notification) can read it:"
                      }
                    </span>
                    <br />
                    Transferring chat for ticket{" "}
                    <span className="text-cornflower-blue-500">
                      {"<<conversation.ticket_id>>"}
                    </span>
                  </div>
                </div>

                <div className="border rounded-lg p-3 bg-gray-50">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="inline-block bg-amber-100 text-amber-700 text-xs font-semibold px-2 py-0.5 rounded">
                      Workflow
                    </span>
                    <span className="text-sm font-medium">
                      Temporary, isolated to this workflow run
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 mb-2">
                    Only exists while the workflow is executing. Cleared when the
                    workflow finishes. Not visible to other workflows. Use for
                    temporary calculations, loop counters, API response data.
                  </p>
                  <div className="bg-white rounded p-2 text-xs font-mono text-gray-600">
                    <span className="text-gray-400">
                      {"// SetData: store API result temporarily"}
                    </span>
                    <br />
                    api_status ={" "}
                    <span className="text-green-600">
                      &quot;approved&quot;
                    </span>
                    <br />
                    <span className="text-gray-400">
                      {"// Condition node checks it, then it's gone"}
                    </span>
                    <br />
                    <span className="text-gray-400">
                      {"// after the workflow completes"}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Where to Use */}
            <div>
              <h4 className="font-semibold text-sm mb-2">Where Variables Work</h4>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="border rounded-lg p-3">
                  <div className="flex items-center gap-2 mb-1">
                    <i
                      className="fa fa-comment text-cornflower-blue-500"
                      aria-hidden="true"
                    />
                    <span className="font-medium">Send Message</span>
                  </div>
                  <p className="text-xs text-gray-500 mb-1">
                    Personalize chatbot responses with stored data.
                  </p>
                  <code className="text-xs bg-gray-50 px-1.5 py-0.5 rounded block">
                    Hi{" "}
                    <span className="text-cornflower-blue-500">
                      {"<<firstName>>"}
                    </span>
                    , how can I help?
                  </code>
                </div>
                <div className="border rounded-lg p-3">
                  <div className="flex items-center gap-2 mb-1">
                    <i
                      className="fa fa-code-branch text-cornflower-blue-500"
                      aria-hidden="true"
                    />
                    <span className="font-medium">Condition Nodes</span>
                  </div>
                  <p className="text-xs text-gray-500 mb-1">
                    Branch workflows based on variable values.
                  </p>
                  <code className="text-xs bg-gray-50 px-1.5 py-0.5 rounded block">
                    If{" "}
                    <span className="text-cornflower-blue-500">
                      customer_tier
                    </span>{" "}
                    equals &quot;premium&quot;
                  </code>
                </div>
                <div className="border rounded-lg p-3">
                  <div className="flex items-center gap-2 mb-1">
                    <i
                      className="fa fa-globe text-cornflower-blue-500"
                      aria-hidden="true"
                    />
                    <span className="font-medium">HTTP Request</span>
                  </div>
                  <p className="text-xs text-gray-500 mb-1">
                    Pass variables in API request bodies.
                  </p>
                  <code className="text-xs bg-gray-50 px-1.5 py-0.5 rounded block whitespace-pre">
                    {`{ "email": "`}
                    <span className="text-cornflower-blue-500">
                      {"<<email>>"}
                    </span>
                    {`" }`}
                  </code>
                </div>
                <div className="border rounded-lg p-3">
                  <div className="flex items-center gap-2 mb-1">
                    <i
                      className="fa fa-envelope text-cornflower-blue-500"
                      aria-hidden="true"
                    />
                    <span className="font-medium">Email Nodes</span>
                  </div>
                  <p className="text-xs text-gray-500 mb-1">
                    Include variables in subject and body.
                  </p>
                  <code className="text-xs bg-gray-50 px-1.5 py-0.5 rounded block">
                    Re: Ticket{" "}
                    <span className="text-cornflower-blue-500">
                      {"<<ticket_id>>"}
                    </span>
                  </code>
                </div>
              </div>
            </div>

            {/* How Variables Get Set — Detailed */}
            <div>
              <h4 className="font-semibold text-sm mb-2">
                How Variables Get Set
              </h4>
              <div className="space-y-4">
                {/* Set Data Node */}
                <div className="border rounded-lg p-3 bg-gray-50">
                  <div className="flex items-center gap-2 mb-2">
                    <i className="fa fa-database text-cornflower-blue-500" aria-hidden="true" />
                    <span className="font-medium text-sm">Set Data Node</span>
                  </div>
                  <p className="text-xs text-gray-600 mb-2">
                    Manually set one or more name/value pairs. Each pair becomes
                    a variable you can reference later. You can add multiple pairs
                    in a single node.
                  </p>
                  <div className="space-y-2 text-xs">
                    <div className="bg-white rounded p-2">
                      <span className="font-medium text-gray-700">Name/Value Pairs:</span>
                      <span className="text-gray-500 ml-1">
                        Add as many as you need. The &quot;Name&quot; becomes the variable key,
                        the &quot;Value&quot; is what gets stored. Values can contain other
                        variables too:{" "}
                        <code className="bg-gray-100 px-1 rounded">{"<<email>>"}</code> in a
                        value resolves at runtime.
                      </span>
                    </div>
                    <div className="bg-white rounded p-2">
                      <span className="font-medium text-gray-700">Scope Toggles:</span>
                      <span className="text-gray-500 ml-1">
                        You can enable multiple scopes at once. If you toggle on both
                        Contact and Conversation, the data saves to both places simultaneously.
                      </span>
                      <div className="mt-1.5 space-y-1 ml-2">
                        <div className="flex items-start gap-1.5">
                          <span className="bg-cornflower-blue-100 text-cornflower-blue-700 px-1.5 py-0.5 rounded font-medium whitespace-nowrap">Contact</span>
                          <span className="text-gray-500">Saved forever. Use for: email, name, preferences, customer tier, company.</span>
                        </div>
                        <div className="flex items-start gap-1.5">
                          <span className="bg-green-100 text-green-700 px-1.5 py-0.5 rounded font-medium whitespace-nowrap">Conversation</span>
                          <span className="text-gray-500">Saved for this chat. Use for: ticket numbers, issue category, routing tags.</span>
                        </div>
                        <div className="flex items-start gap-1.5">
                          <span className="bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded font-medium whitespace-nowrap">Workflow</span>
                          <span className="text-gray-500">Temporary. Use for: API responses, counters, calculations, flags.</span>
                        </div>
                      </div>
                    </div>
                    <div className="bg-white rounded p-2 font-mono text-gray-600">
                      <span className="text-gray-400">{"// Example: Tag a conversation and store an API result"}</span><br />
                      <span className="text-green-600">department</span> = <span className="text-cornflower-blue-500">&quot;billing&quot;</span>
                      <span className="text-gray-400 ml-2">{"// Conversation scope"}</span><br />
                      <span className="text-green-600">priority</span> = <span className="text-cornflower-blue-500">&quot;high&quot;</span>
                      <span className="text-gray-400 ml-2">{"// Conversation scope"}</span><br />
                      <span className="text-green-600">customer_tier</span> = <span className="text-cornflower-blue-500">&quot;enterprise&quot;</span>
                      <span className="text-gray-400 ml-2">{"// Contact scope (persists)"}</span>
                    </div>
                  </div>
                </div>

                {/* Ask Question Node */}
                <div className="border rounded-lg p-3 bg-gray-50">
                  <div className="flex items-center gap-2 mb-2">
                    <i className="fa fa-question-circle text-cornflower-blue-500" aria-hidden="true" />
                    <span className="font-medium text-sm">Ask Question Node</span>
                  </div>
                  <p className="text-xs text-gray-600 mb-2">
                    Asks the visitor a question and automatically saves their reply
                    as a variable. The &quot;Save Reply As&quot; section controls
                    what the variable is named and which scope(s) it saves to.
                  </p>
                  <div className="space-y-2 text-xs">
                    <div className="bg-white rounded p-2">
                      <span className="font-medium text-gray-700">Question Types:</span>
                      <span className="text-gray-500 ml-1">
                        Text (free-form), Multiple Choice, Numeric, Email, Phone.
                        For multiple choice, the selected option value is saved.
                        For email/phone, validation runs before saving.
                      </span>
                    </div>
                    <div className="bg-white rounded p-2">
                      <span className="font-medium text-gray-700">Save Reply As:</span>
                      <span className="text-gray-500 ml-1">
                        Enter an attribute name (e.g., &quot;email&quot;) and toggle on
                        the scope(s). The visitor&apos;s answer is automatically stored under that name.
                        Same scope toggles as Set Data — you can save to multiple scopes at once.
                      </span>
                    </div>
                    <div className="bg-white rounded p-2">
                      <span className="font-medium text-gray-700">Invalid Reply:</span>
                      <span className="text-gray-500 ml-1">
                        Choose &quot;Repeat&quot; to re-ask the question or &quot;Fail Branch&quot;
                        to route to an alternate path when the visitor gives an invalid response.
                      </span>
                    </div>
                    <div className="bg-white rounded p-2 font-mono text-gray-600">
                      <span className="text-gray-400">{"// Example: Collect visitor email"}</span><br />
                      Question: <span className="text-cornflower-blue-500">&quot;What&apos;s your email address?&quot;</span><br />
                      Type: <span className="text-green-600">email</span><br />
                      Save as: <span className="text-green-600">email</span> with Contact scope ON<br />
                      <span className="text-gray-400">{"// Now <<email>> and <<contact.email>> work everywhere"}</span>
                    </div>
                  </div>
                </div>

                {/* Built-in Fields */}
                <div className="border rounded-lg p-3 bg-gray-50">
                  <div className="flex items-center gap-2 mb-2">
                    <i className="fa fa-user text-cornflower-blue-500" aria-hidden="true" />
                    <span className="font-medium text-sm">Built-in Fields</span>
                  </div>
                  <p className="text-xs text-gray-600 mb-1">
                    Always available without setup. These are populated from the
                    contact record and any embed/pre-chat data.
                  </p>
                  <div className="flex flex-wrap gap-1.5 mt-1">
                    {["name", "firstName", "lastName", "email", "phone", "language"].map((f) => (
                      <code key={f} className="bg-white border px-1.5 py-0.5 rounded text-xs font-mono text-gray-600">
                        {"<<"}
                        {f}
                        {">>"}
                      </code>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Resolution Priority */}
            <div>
              <h4 className="font-semibold text-sm mb-2">
                Resolution Priority
              </h4>
              <p className="text-xs text-gray-500 mb-2">
                When a variable name exists in multiple scopes, the system
                checks in this order (first match wins):
              </p>
              <div className="flex gap-2 text-xs">
                <span className="bg-amber-100 text-amber-700 px-2 py-1 rounded font-medium">
                  1. Workflow
                </span>
                <span className="text-gray-400 self-center">&rarr;</span>
                <span className="bg-green-100 text-green-700 px-2 py-1 rounded font-medium">
                  2. Conversation
                </span>
                <span className="text-gray-400 self-center">&rarr;</span>
                <span className="bg-cornflower-blue-100 text-cornflower-blue-700 px-2 py-1 rounded font-medium">
                  3. Contact
                </span>
                <span className="text-gray-400 self-center">&rarr;</span>
                <span className="bg-gray-100 text-gray-700 px-2 py-1 rounded font-medium">
                  4. Built-in
                </span>
              </div>
            </div>

            {/* Persistent Fields */}
            <div>
              <h4 className="font-semibold text-sm mb-2">
                Persistent Fields
              </h4>
              <p className="text-xs text-gray-600">
                When creating a custom field, the &quot;Persistent&quot; toggle
                controls whether the value survives workflow completion and carries
                across language switches. Non-persistent workflow variables are
                cleared as soon as their workflow finishes. Persistent values remain
                available for the lifetime of their scope (e.g., a persistent contact
                field stays forever).
              </p>
            </div>

            {/* Real-World Examples */}
            <div>
              <h4 className="font-semibold text-sm mb-3">
                Workflow Examples
              </h4>
              <div className="space-y-4">
                {/* Example 1: Support Ticket */}
                <div className="border rounded-lg p-3 bg-gray-50">
                  <p className="text-xs font-semibold text-gray-700 mb-2">
                    Support Ticket Routing
                  </p>
                  <div className="text-xs space-y-1.5">
                    <div className="flex items-start gap-2">
                      <span className="bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded font-medium whitespace-nowrap">1</span>
                      <span className="text-gray-600">
                        <strong>Ask Question:</strong> &quot;What&apos;s your email?&quot;
                        &rarr; Save as <code className="bg-white px-1 rounded">email</code> (Contact)
                      </span>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded font-medium whitespace-nowrap">2</span>
                      <span className="text-gray-600">
                        <strong>Ask Question:</strong> &quot;What do you need help with?&quot;
                        (Multiple choice: Billing, Technical, Account)
                        &rarr; Save as <code className="bg-white px-1 rounded">issue_type</code> (Conversation)
                      </span>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded font-medium whitespace-nowrap">3</span>
                      <span className="text-gray-600">
                        <strong>Set Data:</strong>{" "}
                        <code className="bg-white px-1 rounded">ticket_id</code> = &quot;TKT-&quot; + timestamp (Conversation)
                      </span>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded font-medium whitespace-nowrap">4</span>
                      <span className="text-gray-600">
                        <strong>Condition:</strong> If{" "}
                        <code className="bg-white px-1 rounded">issue_type</code> = &quot;Technical&quot;
                        &rarr; Transfer to AI for troubleshooting
                      </span>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded font-medium whitespace-nowrap">5</span>
                      <span className="text-gray-600">
                        <strong>Transfer to AI:</strong> AI receives all variables
                        (email, issue_type, ticket_id) automatically in its context
                      </span>
                    </div>
                  </div>
                </div>

                {/* Example 2: E-commerce */}
                <div className="border rounded-lg p-3 bg-gray-50">
                  <p className="text-xs font-semibold text-gray-700 mb-2">
                    E-commerce Order Lookup
                  </p>
                  <div className="text-xs space-y-1.5">
                    <div className="flex items-start gap-2">
                      <span className="bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded font-medium whitespace-nowrap">1</span>
                      <span className="text-gray-600">
                        <strong>Ask Question:</strong> &quot;What&apos;s your order number?&quot;
                        &rarr; Save as <code className="bg-white px-1 rounded">order_id</code> (Conversation)
                      </span>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded font-medium whitespace-nowrap">2</span>
                      <span className="text-gray-600">
                        <strong>HTTP Request:</strong> GET{" "}
                        <code className="bg-white px-1 rounded">
                          {"https://api.store.com/orders/<<order_id>>"}
                        </code>
                        &rarr; response saved to workflow variables
                      </span>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded font-medium whitespace-nowrap">3</span>
                      <span className="text-gray-600">
                        <strong>Set Data:</strong>{" "}
                        <code className="bg-white px-1 rounded">order_status</code> = response value (Conversation)
                      </span>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded font-medium whitespace-nowrap">4</span>
                      <span className="text-gray-600">
                        <strong>Send Message:</strong> &quot;Order{" "}
                        <code className="bg-white px-1 rounded">{"<<order_id>>"}</code> status:{" "}
                        <code className="bg-white px-1 rounded">{"<<order_status>>"}</code>&quot;
                      </span>
                    </div>
                  </div>
                </div>

                {/* Example 3: Lead Qualification */}
                <div className="border rounded-lg p-3 bg-gray-50">
                  <p className="text-xs font-semibold text-gray-700 mb-2">
                    Lead Qualification + CRM Sync
                  </p>
                  <div className="text-xs space-y-1.5">
                    <div className="flex items-start gap-2">
                      <span className="bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded font-medium whitespace-nowrap">1</span>
                      <span className="text-gray-600">
                        <strong>Ask Question:</strong> &quot;What&apos;s your name?&quot;
                        &rarr; Save as <code className="bg-white px-1 rounded">name</code> (Contact)
                      </span>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded font-medium whitespace-nowrap">2</span>
                      <span className="text-gray-600">
                        <strong>Ask Question:</strong> &quot;Company size?&quot;
                        (Multiple choice: 1-10, 11-50, 51-200, 200+)
                        &rarr; Save as <code className="bg-white px-1 rounded">company_size</code> (Contact)
                      </span>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded font-medium whitespace-nowrap">3</span>
                      <span className="text-gray-600">
                        <strong>Set Data:</strong>{" "}
                        <code className="bg-white px-1 rounded">lead_source</code> = &quot;website_chat&quot; (Contact),{" "}
                        <code className="bg-white px-1 rounded">qualified</code> = &quot;true&quot; (Conversation)
                      </span>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded font-medium whitespace-nowrap">4</span>
                      <span className="text-gray-600">
                        <strong>HTTP Request:</strong> POST to CRM:{" "}
                        <code className="bg-white px-1 rounded">
                          {'{ "name": "<<name>>", "size": "<<company_size>>", "source": "<<lead_source>>" }'}
                        </code>
                      </span>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded font-medium whitespace-nowrap">5</span>
                      <span className="text-gray-600">
                        <strong>Email:</strong> Send notification to sales@company.com with
                        subject &quot;New lead:{" "}
                        <code className="bg-white px-1 rounded">{"<<name>>"}</code>&quot;
                      </span>
                    </div>
                  </div>
                </div>

                {/* Example 4: AI Handoff */}
                <div className="border rounded-lg p-3 bg-gray-50">
                  <p className="text-xs font-semibold text-gray-700 mb-2">
                    Collect Info Then Hand Off to AI
                  </p>
                  <div className="text-xs space-y-1.5">
                    <div className="flex items-start gap-2">
                      <span className="bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded font-medium whitespace-nowrap">1</span>
                      <span className="text-gray-600">
                        <strong>Ask Question:</strong> &quot;Hi! What&apos;s your name?&quot;
                        &rarr; Save as <code className="bg-white px-1 rounded">firstName</code> (Contact)
                      </span>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded font-medium whitespace-nowrap">2</span>
                      <span className="text-gray-600">
                        <strong>Set Data:</strong>{" "}
                        <code className="bg-white px-1 rounded">customer_tier</code> = &quot;premium&quot; (Contact),{" "}
                        <code className="bg-white px-1 rounded">department</code> = &quot;support&quot; (Conversation)
                      </span>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded font-medium whitespace-nowrap">3</span>
                      <span className="text-gray-600">
                        <strong>Transfer to AI:</strong> The AI automatically receives
                        firstName, customer_tier, department, and all other variables.
                        It knows who the customer is and can personalize responses.
                      </span>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mt-2 italic">
                    All contact and conversation variables are included in the AI&apos;s
                    context. No extra setup needed — just set the data before the
                    Transfer to AI node.
                  </p>
                </div>
              </div>
            </div>

            {/* Quick Copy */}
            {fields.length > 0 && (
              <div>
                <h4 className="font-semibold text-sm mb-2">
                  Your Variables (click to copy)
                </h4>
                <div className="flex flex-wrap gap-1.5">
                  {fields.map((f) => (
                    <button
                      key={f.id}
                      className="bg-gray-100 hover:bg-cornflower-blue-100 px-2 py-1 rounded text-xs font-mono transition-colors"
                      title={`Click to copy <<${f.name}>>`}
                      aria-label={`Copy variable ${f.name}`}
                      onClick={() => copyVariable(f.name)}
                    >
                      {"<<"}
                      {f.name}
                      {">>"}
                      {copiedName === f.name && (
                        <i
                          className="fa fa-check text-green-600 ml-1"
                          aria-hidden="true"
                        />
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {showAddFieldModal && (
        <AddFieldModal
          show={showAddFieldModal}
          onCancel={() => setShowAddFieldModal(false)}
          submit={submit}
        />
      )}

      {editFieldId != null && (
        <EditFieldModal
          field={fields.find((f) => f.id === editFieldId)!}
          onCancel={() => setEditFieldId(undefined)}
          onSave={(def) => handleUpdate(editFieldId, def)}
        />
      )}
    </div>
  );
}
